/**
 * Push-notification plumbing for BarChain client.
 *
 * Requires:
 *   - public/firebase-messaging-sw.js at origin root
 *   - A VAPID key generated in Firebase Console → Project Settings → Cloud Messaging
 *     (Web Push certificates). Paste it below or set VITE_FCM_VAPID_KEY at build time.
 *
 * The Firebase Function in functions/index.js fires the pushes. Tokens are persisted
 * in Firestore at `users/{uid}/fcmTokens/{token}`.
 */
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { app, db } from '../../data/firebase/config';

// VAPID public key for FCM Web Push. Safe to ship in client bundles.
// Rotate from Firebase Console → Cloud Messaging → Web Push certificates and replace this value.
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY
  || 'BEKw4YHf4PPbuZFjKC2vin8_d_OZtk_sdU6xRgOgO4RESkmlPmIG2m7ZYJSf4MdyhUAef8759_NRcysPXPZN6Q4';

let messagingInstance = null;

async function getMessagingSafe() {
  if (messagingInstance) return messagingInstance;
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

/**
 * Ask for permission + register for push. Persist the token under the user.
 *
 * @returns {Promise<{status: 'granted'|'denied'|'default'|'unsupported', token?: string}>}
 */
export async function enablePushForUser(uid) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return { status: 'unsupported' };
  }
  if (!VAPID_KEY) {
    return { status: 'unsupported' };
  }
  const m = await getMessagingSafe();
  if (!m) return { status: 'unsupported' };

  // Request permission
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { status: perm };

  // Register the SW explicitly at its known path
  const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
  if (!token) return { status: 'denied' };

  // Persist the token under the user's fcmTokens subcollection.
  await setDoc(doc(db, `users/${uid}/fcmTokens`, token), {
    createdAt: serverTimestamp(),
    userAgent: navigator.userAgent,
  }, { merge: true });

  return { status: 'granted', token };
}

/**
 * Subscribe to foreground push messages.
 * Return value is the unsubscribe function.
 */
export async function onForegroundPush(handler) {
  const m = await getMessagingSafe();
  if (!m) return () => {};
  return onMessage(m, handler);
}

/** Simple detection used by UI to show/hide the opt-in button. */
export function canAskPushPermission() {
  return typeof Notification !== 'undefined'
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && Boolean(VAPID_KEY);
}

export function currentPushPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
